export type DictationPermissionStatus = {
  microphoneGranted?: boolean;
  overlayGranted?: boolean;
  accessibilityGranted?: boolean;
  systemAudioGranted?: boolean;
};

export type AndroidSettingsFlavor = "samsung" | "generic";

export const getAndroidSettingsFlavor = (
  manufacturer?: string,
): AndroidSettingsFlavor =>
  manufacturer?.trim().toLowerCase().includes("samsung")
    ? "samsung"
    : "generic";

export type AndroidDictationStep =
  | "intro"
  | "microphone"
  | "overlay"
  | "accessibility"
  | "done";

export const androidDictationSteps: readonly AndroidDictationStep[] = [
  "intro",
  "microphone",
  "overlay",
  "accessibility",
  "done",
];

export const getAndroidDictationResumeStep = (
  status?: DictationPermissionStatus,
): AndroidDictationStep => {
  if (!status) return "intro";
  const hasStarted = Boolean(
    status.microphoneGranted ||
    status.overlayGranted ||
    status.accessibilityGranted,
  );
  if (!hasStarted) return "intro";
  if (!status.microphoneGranted) return "microphone";
  if (!status.overlayGranted) return "overlay";
  if (!status.accessibilityGranted) return "accessibility";
  return "done";
};

export const getAndroidDictationOpeningStep = (
  introCompleted: boolean,
  resumeStep: AndroidDictationStep,
): AndroidDictationStep =>
  introCompleted
    ? resumeStep === "intro"
      ? "microphone"
      : resumeStep
    : "intro";

export const getVisibleAndroidDictationSteps = (
  status?: DictationPermissionStatus,
): AndroidDictationStep[] =>
  androidDictationSteps.filter((step) => {
    if (step === "microphone") return !status?.microphoneGranted;
    if (step === "overlay") return !status?.overlayGranted;
    if (step === "accessibility") return !status?.accessibilityGranted;
    return true;
  });

const _adjacentStep = <Step extends string>(
  steps: readonly Step[],
  current: Step,
  offset: -1 | 1,
): Step => {
  const index = steps.indexOf(current);
  const target = index + offset;
  return index < 0 || target < 0 || target >= steps.length
    ? current
    : steps[target];
};

export const getPreviousDictationStep = <Step extends string>(
  steps: readonly Step[],
  current: Step,
): Step => _adjacentStep(steps, current, -1);

export const getNextDictationStep = <Step extends string>(
  steps: readonly Step[],
  current: Step,
): Step => _adjacentStep(steps, current, 1);

export type AndroidPrimaryEffect =
  | "advance"
  | "requestMicrophone"
  | "openOverlaySettings"
  | "openAccessibilityDisclosure"
  | "complete";

export const getAndroidPrimaryEffect = (
  step: AndroidDictationStep,
  status?: DictationPermissionStatus,
): AndroidPrimaryEffect => {
  if (step === "microphone" && !status?.microphoneGranted) {
    return "requestMicrophone";
  }
  if (step === "overlay" && !status?.overlayGranted) {
    return "openOverlaySettings";
  }
  if (step === "accessibility" && !status?.accessibilityGranted) {
    return "openAccessibilityDisclosure";
  }
  if (step === "done") return "complete";
  return "advance";
};

export type IosDictationStep =
  | "intro"
  | "microphone"
  | "keyboard"
  | "tryit"
  | "done";

export const iosDictationSteps: readonly IosDictationStep[] = [
  "intro",
  "microphone",
  "keyboard",
  "tryit",
  "done",
];

export const getIosResumeStep = (
  current: IosDictationStep,
  status?: { microphoneGranted?: boolean; keyboardEnabled?: boolean },
): IosDictationStep =>
  current === "intro" && status?.microphoneGranted && status.keyboardEnabled
    ? "keyboard"
    : current;

export const getIosKeyboardEffect = (
  keyboardAdded: boolean,
): "continue" | "openSettings" => (keyboardAdded ? "continue" : "openSettings");

export const hasCompletedDictationTryIt = (text: string): boolean =>
  text.trim().length > 0;

export type DesktopDictationStep =
  | "persona"
  | "intro"
  | "permissions"
  | "record"
  | "dictate"
  | "microphone"
  | "systemAudio"
  | "dictationMicrophone"
  | "accessibility";

export const getDesktopSetupOpeningStep = (
  entry?: DesktopDictationStep,
  resumeSystemAudio = false,
): "persona" | "intro" | "permissions" =>
  resumeSystemAudio
    ? "permissions"
    : entry && entry !== "intro" && entry !== "persona"
      ? "permissions"
      : "persona";

export const getDesktopSetupPermissions = (
  platform: "macos" | "windows",
  systemAudio?: string,
): readonly ("microphone" | "systemAudio" | "accessibility")[] =>
  platform === "windows"
    ? systemAudio === "denied"
      ? ["microphone", "systemAudio"]
      : ["microphone"]
    : systemAudio === "unsupported"
      ? ["microphone", "accessibility"]
      : ["microphone", "systemAudio", "accessibility"];

export const getDesktopHomeJourney = (input: {
  intent?: string | null;
  dictationReady: boolean;
  recordingReady: boolean;
  recordingSupported: boolean;
  hasAccount: boolean;
}) => {
  const order =
    input.intent === "record"
      ? (["record", "dictate", "account"] as const)
      : (["dictate", "record", "account"] as const);
  const steps = order.filter(
    (step) => step !== "record" || input.recordingSupported,
  );
  const completed = {
    dictate: input.dictationReady,
    record: input.recordingReady,
    account: input.hasAccount,
  };
  return {
    nextStep: steps.find((step) => !completed[step]),
    completedCount: steps.filter((step) => completed[step]).length,
    totalCount: steps.length,
  };
};

export const isDesktopDictationSetupComplete = (
  status?: DictationPermissionStatus,
): boolean => Boolean(status?.microphoneGranted && status.accessibilityGranted);

export const canAcceptAccessibilityDisclosure = (input: {
  checked: boolean;
  busy: boolean;
}): boolean => input.checked && !input.busy;

export const getDictationBubbleDestination = (
  setupComplete: boolean,
): "setup" | "status" => (setupComplete ? "status" : "setup");

export const getNextEnabledValue = (
  enabled: boolean,
  busy: boolean,
): boolean | undefined => (busy ? undefined : !enabled);

export const stopDictationFlowSession = (
  stop: () => Promise<unknown>,
  dismiss: () => void,
): void => {
  void stop().catch(() => undefined);
  dismiss();
};

// ── Custom vocabulary ───────────────────────────────────────────────────────
// Dictated text never passes through JS (both platforms transcribe and
// insert natively), so custom vocabulary is composed here into a serialized
// rule table and shipped to native via GlobalDictation.configure. Natives
// apply it right before insertion (DictationVocabulary.java /
// DictationInsertionFormatter.applyVocabulary).

export type DictationVocabularyEntry = { from: string; to: string };

export type DictationVocabularySettings = {
  entries: DictationVocabularyEntry[];
};

export const defaultDictationVocabulary: DictationVocabularySettings = {
  entries: [],
};

type DictationVocabularyRule = {
  from: string;
  to: string;
  kind: "text";
};

// Serialized custom rules for native. Returns "" when there is nothing to
// apply, which also clears any legacy built-in punctuation rules.
export const composeDictationVocabularyRules = (
  vocabulary: DictationVocabularySettings | undefined,
): string => {
  const settings = vocabulary ?? defaultDictationVocabulary;
  const rules: DictationVocabularyRule[] = settings.entries
    .filter((entry) => entry.from.trim().length > 0)
    .map((entry) => ({
      from: entry.from.trim(),
      to: entry.to,
      kind: "text",
    }));
  return rules.length > 0 ? JSON.stringify(rules) : "";
};

export const NATIVE_HOME_JOURNEY_STEPS = [
  "dictation",
  "account",
  "desktop",
] as const;

export type NativeHomeJourneyStep =
  (typeof NATIVE_HOME_JOURNEY_STEPS)[number];

export const getNativeHomeJourney = (facts: {
  dictationSetupComplete: boolean;
  hasAccount: boolean;
  connectedClients?: readonly string[];
}) => {
  const completedSteps = NATIVE_HOME_JOURNEY_STEPS.filter((step) => {
    if (step === "dictation") return facts.dictationSetupComplete;
    if (step === "account") return facts.hasAccount;
    return Boolean(
      facts.connectedClients?.some(
        (client) =>
          client === "macDesktop" ||
          client === "windowsDesktop" ||
          client === "chromeExtension",
      ),
    );
  });
  const nextStep = NATIVE_HOME_JOURNEY_STEPS.find(
    (step) => !completedSteps.includes(step),
  );

  return {
    completedSteps,
    completedCount: completedSteps.length,
    nextStep,
    isComplete: completedSteps.length === NATIVE_HOME_JOURNEY_STEPS.length,
  };
};
